import React from 'react';
import { useLoaderData, Link } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  CircleStackIcon,
  DocumentTextIcon,
  ChevronRightIcon,
  ShareIcon
} from '@heroicons/react/24/outline';
import { PageTitle } from '@oysterjs/ui/page';
import { Input, InputGroup } from '@oysterjs/ui/input';
import { Field, FieldGroup, Fieldset, Label } from '@oysterjs/ui/fieldset';
import { KnowledgeBaseAccessRole, KnowledgeBaseListFieldsFragment } from '~/api/graphql';
import { Await } from '@oysterjs/ui/async';
import { Badge } from '@oysterjs/ui/badge';

const RoleBadge = ({ role }: { role: KnowledgeBaseAccessRole }) => {
  switch (role) {
    case KnowledgeBaseAccessRole.Contributor:
      return <Badge color="sky">Contributor</Badge>;
    case KnowledgeBaseAccessRole.Viewer:
      return <Badge color="sky">View only</Badge>;
    default:
      return <Badge color="purple">{role}</Badge>;
  }
};

interface KnowledgeBaseCardProps {
  knowledgeBase: KnowledgeBaseListFieldsFragment;
}

const KnowledgeBaseCard = ({ knowledgeBase }: KnowledgeBaseCardProps) => {
  const documentCount = knowledgeBase.documents?.length || 0;
  const isShared = !knowledgeBase.isOwner;

  return (
    <Link
      to={`/app/knowledge/${knowledgeBase.id}`}
      className="group text-left p-6 rounded-lg bg-white dark:bg-neutral-800 shadow hover:shadow-xl transition-all duration-300 w-full h-full"
    >
      <div className="flex items-start justify-between">
        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-sky-50 dark:bg-sky-500/10 text-primary-700 dark:text-primary-300">
          {isShared ? <ShareIcon className="size-6" /> : <CircleStackIcon className="size-6" />}
        </div>
        <div className="flex flex-col items-end gap-1">
          {isShared && knowledgeBase.myAccess && <RoleBadge role={knowledgeBase.myAccess.role} />}
        </div>
      </div>

      <div className="mt-4 flex-1">
        <h3 className="text-base/6 font-body m-0 p-0 font-medium flex flex-row items-center gap-1.5">
          {knowledgeBase.title}
        </h3>
        {isShared && knowledgeBase.ownerAgency && (
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            Shared by {knowledgeBase.ownerAgency.name}
          </p>
        )}
        <div className="mt-2 flex items-center gap-1.5 text-sm text-neutral-500 dark:text-neutral-400">
          <DocumentTextIcon className="h-4 w-4" />
          <span>
            {documentCount} {documentCount === 1 ? 'document' : 'documents'}
          </span>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between border-t border-neutral-100 dark:border-neutral-700 pt-4">
        <span className="text-xs text-neutral-500 dark:text-neutral-400">
          Updated{' '}
          {new Intl.DateTimeFormat('en-US', {
            dateStyle: 'medium'
          }).format(new Date(knowledgeBase.updatedAt))}
        </span>
        <ChevronRightIcon className="h-5 w-5 text-neutral-500 dark:text-neutral-400" />
      </div>
    </Link>
  );
};

export const KnowledgeBasesListPage = () => {
  const [filter, setFilter] = React.useState('');
  const loader = useLoaderData() as
    | { data: Promise<{ knowledgeBases?: KnowledgeBaseListFieldsFragment[] }> }
    | undefined;

  const filterKnowledgeBase = (kb: KnowledgeBaseListFieldsFragment) => {
    if (!filter) {
      return true;
    }
    return kb.title.toLowerCase().includes(filter.toLowerCase());
  };

  return (
    <PageTitle
      title="Knowledge Bases"
      description="Select a knowledge base to view and manage documents"
    >
      <div className="space-y-6">
        <div className="flex justify-between gap-4 flex-col sm:flex-row">
          <form className="w-full max-w-md">
            <Fieldset>
              <FieldGroup>
                <Field>
                  <Label className="sr-only" data-slot="none">
                    Filter knowledge bases
                  </Label>
                  <InputGroup>
                    <MagnifyingGlassIcon className="h-4 w-4" />
                    <Input
                      type="search"
                      name="search"
                      placeholder="Search knowledge bases..."
                      value={filter}
                      onChange={(e) => setFilter(e.currentTarget.value.trim())}
                    />
                  </InputGroup>
                </Field>
              </FieldGroup>
            </Fieldset>
          </form>
        </div>

        <React.Suspense fallback={<KnowledgeBasesListLoader />}>
          <Await resolve={loader?.data}>
            {(data) => {
              const allKnowledgeBases = data?.knowledgeBases || [];

              const knowledgeBases = allKnowledgeBases.filter(filterKnowledgeBase);

              if (knowledgeBases.length === 0 && filter) {
                return (
                  <div className="text-center py-12 bg-white dark:bg-neutral-800 rounded-lg shadow">
                    <MagnifyingGlassIcon className="mx-auto h-12 w-12 text-neutral-400 dark:text-neutral-500" />
                    <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
                      No knowledge bases found
                    </h3>
                    <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                      No knowledge bases match your search. Try a different term.
                    </p>
                  </div>
                );
              }

              return (
                <div className="grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
                  {knowledgeBases.map((kb) => (
                    <KnowledgeBaseCard key={kb.id} knowledgeBase={kb} />
                  ))}
                </div>
              );
            }}
          </Await>
        </React.Suspense>
      </div>
    </PageTitle>
  );
};

const KnowledgeBasesListLoader = () => {
  return (
    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="flex flex-col rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-6 shadow-sm animate-pulse"
        >
          <div className="flex items-start justify-between">
            <div className="h-12 w-12 rounded-lg bg-neutral-100 dark:bg-neutral-700" />
            <div className="h-5 w-16 rounded-full bg-neutral-100 dark:bg-neutral-700" />
          </div>
          <div className="mt-4 flex-1">
            <div className="h-6 w-3/4 rounded bg-neutral-100 dark:bg-neutral-700" />
            <div className="mt-2 h-4 w-1/2 rounded bg-neutral-100 dark:bg-neutral-700" />
          </div>
          <div className="mt-4 border-t border-neutral-100 dark:border-neutral-700 pt-4">
            <div className="h-4 w-1/3 rounded bg-neutral-100 dark:bg-neutral-700" />
          </div>
        </div>
      ))}
    </div>
  );
};
